import {
  CForm,
  CFormGroup,
  CLabel,
  CModalBody,
  CModalFooter,
  CModalHeader,
} from "@coreui/react";
import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  BasicInput,
  BasicNumberInput,
  BasicSelect,
  ButtonBasic,
  ErrorText,
} from "src/components";
import StyledModal from "src/components/BasicModal/BasicModal.style";
import { DB_PATHS } from "src/constants";
import { useToasts, useUpdateDatabase } from "src/hooks";
import { useEsgContext } from "../../context";
import OpenQuestionInputs from "./OpenQuestionInputs";
import SelectQuestionInputs from "./SelectQuestionInputs";
import TextInput from "./TextInput";

// const QUESTION_TYPES = ["text", "number", "select", "date", "date-range"];
const QUESTION_TYPES = [
  { value: "select", label: "Select" },
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "date-range", label: "Date Range" },
];

const EditModal = ({
  formMethods,
  showModal,
  closeModal,
  questionPath,
  isNew,
  isUsingGlobal,
  setRefresh,
  currentVersionId,
}) => {
  const { orgId, updateSurveyVersion } = useEsgContext();

  const {
    // handleSubmit,
    setValue: setValueUpper,
    formState: { errors },
    getValues,
  } = formMethods;

  const { reset, control, handleSubmit, setValue, watch } = useForm();
  const [initValues, setInitValues] = useState({});

  useEffect(() => {
    if (!questionPath) return;
    const question = getValues(questionPath);
    setInitValues(cloneDeep(question));
    reset(question);
  }, [questionPath]);

  const questionType = watch("type");

  const isSelectQuestion = !questionType || questionType === "select";

  const question = questionPath ? getValues(questionPath) : null;

  const { tryAndNotify } = useToasts();

  const [answersError, setAnswersError] = useState("");

  const onSubmit = async (data) => {
    if (!data?.answers) return;
    const areAnswerValuesUnique = data.answers
      .map((a) => a.value)
      .every((value, index, self) => self.indexOf(value) === index);

    if (!areAnswerValuesUnique) {
      setAnswersError(
        "Answer points must be unique. If you want the question to be unscored, set the weight to 0."
      );
      return;
    } else {
      setAnswersError("");
    }

    setValueUpper(questionPath, data);

    const upperData = cloneDeep(getValues());
    upperData.areas.forEach((area) => {
      area.questions = area.questions.filter(Boolean);
    });

    await tryAndNotify(
      async () =>
        updateSurveyVersion({
          data: upperData,
          isUsingGlobal,
          orgId,
          currentVersionId,
        }),
      "Survey updated successfully",
      "Error updating survey"
    );

    closeModal();
    if (isUsingGlobal) {
      setRefresh((prev) => ++prev);
    }
  };

  const handleChange = (e) => {
    setValue(e.target.name, e.target.value, { shouldTouch: true });
  };

  const handleClose = () => {
    if (!isNew) {
      setValueUpper(questionPath, initValues);
    } else {
      setValueUpper(questionPath, null);
    }
    closeModal();
  };

  const handleDelete = async () => {
    setValueUpper(questionPath, null);
    const upperData = cloneDeep(getValues());


    upperData.areas.forEach((area) => {
      area.questions = area.questions.filter(Boolean);

      area.questions.forEach((question) => {
        if (question.type === "select") {
          question.answers = question.answers.filter(Boolean);

          const hasNonUniqueValues = question.answers
            .map((a) => a.value)
            .some((answer, index, self) => index !== self.lastIndexOf(answer));

          if (hasNonUniqueValues) {
            for (let i = 0; i < question.answers.length; i++) {
              let duplicateCount = 0;
              for (let j = i + 1; j < question.answers.length; j++) {
                if (question.answers[i].value === question.answers[j].value) {
                  // unicode "a" is 97
                  const char = String.fromCharCode(97 + duplicateCount);
                  question.answers[j].value = question.answers[j].value + char;
                  duplicateCount++;
                }
              }
            }
          }
        }
      });
    });

    await tryAndNotify(
      async () =>
        updateSurveyVersion({
          data: upperData,
          isUsingGlobal,
          orgId,
          currentVersionId,
        }),
      "Survey updated successfully",
      "Error updating survey"
    );

    closeModal();
    if (isUsingGlobal) {
      setRefresh((prev) => ++prev);
    }
  };

  const isNumberQuestion = questionType === "number";

  return (
    <StyledModal size="xl" show={showModal} onClose={handleClose} centered>
      <CModalHeader closeButton className="d-flex justify-content-center pb-0">
        <h3 className="my-4 px-4">{isNew ? "Add" : "Edit"} question</h3>
      </CModalHeader>
      <CForm onSubmit={handleSubmit(onSubmit)}>
        {question && (
          <CModalBody
            className="px-5 pb-5 pt-0 d-flex flex-column"
            style={{ gap: "1rem" }}
          >
            <CFormGroup>
              <CLabel className="font-bold" htmlFor="type">
                Question Type
              </CLabel>
              <BasicSelect
                id="type"
                name="type"
                options={QUESTION_TYPES}
                onChange={handleChange}
                value={questionType || "select"}
              />
            </CFormGroup>

            {isNumberQuestion && (
              <>
                <CFormGroup>
                  <CLabel htmlFor={"min"} className={"font-bold"}>
                    Min
                  </CLabel>
                  <BasicNumberInput
                    id={"min"}
                    name="min"
                    defaultValue={question?.min || 0}
                    onChange={handleChange}
                  />
                </CFormGroup>
                <CFormGroup>
                  <CLabel htmlFor={"max"} className={"font-bold"}>
                    Max
                  </CLabel>
                  <BasicNumberInput
                    id={"max"}
                    name="max"
                    defaultValue={question?.max || 0}
                    onChange={handleChange}
                  />
                </CFormGroup>
                <CFormGroup>
                  <CLabel htmlFor={"suffix"} className={"font-bold"}>
                    Suffix
                  </CLabel>
                  <BasicInput
                    id={"suffix"}
                    name="suffix"
                    defaultValue={question?.suffix || ""}
                    onChange={handleChange}
                  />
                </CFormGroup>
              </>
            )}

            {isSelectQuestion ? (
              <SelectQuestionInputs
                question={question}
                handleChange={handleChange}
                errors={errors}
                control={control}
              />
            ) : (
              <OpenQuestionInputs
                question={question}
                handleChange={handleChange}
                errors={errors}
              />
            )}
            <TextInput
              handleChange={handleChange}
              name={`guidance`}
              value={question?.guidance}
              errors={errors}
              label={"Guidance and detail"}
              labelClassname={"font-bold"}
            />
            {isUsingGlobal && (
              <p className="text-danger mb-0">
                WARNING: You are editing a global version of the survey. It may
                be used by multiple organisations.
              </p>
            )}
            <ErrorText value={answersError} customMessage={answersError} />
          </CModalBody>
        )}

        <CModalFooter>
          <ButtonBasic
            className={"mr-auto"}
            onClick={handleDelete}
            type="button"
            bgcolor="#ff4136"
            hovercolor="darkred"
          >
            Delete
          </ButtonBasic>

          <ButtonBasic
            onClick={handleClose}
            type="button"
            bgcolor="var(--secondary)"
            hovercolor="var(--secondary-dark)"
          >
            Cancel
          </ButtonBasic>
          <ButtonBasic type="submit" bgcolor="green" hovercolor="darkgreen">
            Save
          </ButtonBasic>
        </CModalFooter>
      </CForm>
    </StyledModal>
  );
};

export default EditModal;
